import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, ShieldOff, Lock, Loader2, Save, MapPin, Navigation } from "lucide-react";

import VendorFullRentalTermsEditor, { RENTAL_TERMS_SECTIONS } from "@/components/vendor/VendorFullRentalTermsEditor";

/**
 * Vendor-side editor for the 3 policy texts surfaced on the public
 * /vendors/:vendor_id/policies FAQ page. Each field is independent — leaving any blank
 * falls back to the platform default. Saved via the existing
 * `PATCH /api/users/me/business` endpoint (same one used for address, hours, etc.).
 *
 * Iter 67dg — the legacy "Terms & Conditions" textarea is now an
 * 8-section accordion ("Full rental terms"). Cancellation and Privacy
 * remain plain textareas because they're legal documents not
 * commonly broken up that way.
 */
const POLICY_DEFS = [
  { key: "cancellation_policy", label: "Cancellation & Refund", icon: ShieldOff, placeholder: "Describe your refund tiers — e.g. 48h before pickup = 100%, 24h = 50%, no-show = 0%. Leave blank to use the RentalCar.al default." },
  { key: "privacy_policy", label: "Privacy Policy", icon: Lock, placeholder: "Explain how you collect, store, and use renter data. Leave blank to use the RentalCar.al default." },
];

export default function VendorPoliciesEditor() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    terms_and_conditions: "",
    cancellation_policy: "",
    privacy_policy: "",
    pickup_instructions: "",
    pickup_map_url: "",
    dropoff_instructions: "",
    dropoff_map_url: "",
    // Iter 67dg — Full-rental-terms structured sections.
    terms_included: "",
    terms_not_included: "",
    terms_pickup_requirements: "",
    terms_deposits_excess: "",
    terms_fuel_policy: "",
    terms_extra_services: "",
    terms_extra_equipment: "",
    terms_important_info: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      terms_and_conditions: user.terms_and_conditions || "",
      cancellation_policy: user.cancellation_policy || "",
      privacy_policy: user.privacy_policy || "",
      pickup_instructions: user.pickup_instructions || "",
      pickup_map_url: user.pickup_map_url || "",
      dropoff_instructions: user.dropoff_instructions || "",
      dropoff_map_url: user.dropoff_map_url || "",
      // Iter 67dg
      terms_included: user.terms_included || "",
      terms_not_included: user.terms_not_included || "",
      terms_pickup_requirements: user.terms_pickup_requirements || "",
      terms_deposits_excess: user.terms_deposits_excess || "",
      terms_fuel_policy: user.terms_fuel_policy || "",
      terms_extra_services: user.terms_extra_services || "",
      terms_extra_equipment: user.terms_extra_equipment || "",
      terms_important_info: user.terms_important_info || "",
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/business", form);
      toast.success("Policies saved");
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const publicUrl = user ? `${window.location.origin}/vendors/${user.id}/policies` : "";

  return (
    <div className="rounded-3xl border border-border bg-white p-6 mb-8" data-testid="vendor-policies-editor">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-lg font-medium">Policies & legal</h2>
          <p className="text-xs text-neutral-500 max-w-xl">
            Renters see these on a dedicated FAQ-style page linked from every one of your cars and from the rental agreement. Leave a field blank to fall back to the RentalCar.al platform default.
          </p>
        </div>
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline shrink-0" data-testid="vendor-policies-public-link">
            View public page →
          </a>
        )}
      </div>
      <div className="space-y-5 max-w-3xl">
        {/* Iter 67dg — Full rental terms (was: a single 'Terms & Conditions' textarea) */}
        <div>
          <Label className="flex items-center gap-2 mb-1.5">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium">Full rental terms</span>
            <span className="text-[10px] uppercase tracking-wider text-blue-700 bg-blue-50 ring-1 ring-blue-100 rounded-full px-2 py-0.5">
              8 sections
            </span>
          </Label>
          <p className="text-xs text-neutral-500 mb-2">
            Break your rental terms into 8 short, scannable sections. Each panel appears separately on your public policies page so renters can quickly find what they need.
          </p>
          <VendorFullRentalTermsEditor form={form} setForm={setForm} />
        </div>

        {POLICY_DEFS.map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key}>
            <Label className="flex items-center gap-2 mb-1.5">
              <Icon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">{label}</span>
              {!(form[key] || "").trim() && (
                <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Using default</span>
              )}
            </Label>
            <textarea
              value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              placeholder={placeholder}
              rows={6}
              className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid={`vendor-policy-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-border max-w-3xl">
        <div className="mb-4">
          <h3 className="font-medium flex items-center gap-2"><Navigation className="h-4 w-4 text-blue-600" /> Pickup & drop-off</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Renters see these on your policies page and in their booking confirmation email. Paste a Google Maps URL — either the standard share link (<code className="text-[10px] bg-neutral-100 px-1 rounded">maps.app.goo.gl/…</code>) or, ideally, an "Embed a map" iframe URL (Google Maps → <em>Share</em> → <em>Embed a map</em> → copy the <code className="text-[10px] bg-neutral-100 px-1 rounded">src=</code> URL). Embed URLs render an interactive mini-map directly on the page.
          </p>
        </div>
        <div className="space-y-5">
          <div>
            <Label className="flex items-center gap-2 mb-1.5">
              <MapPin className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">Pickup instructions</span>
            </Label>
            <textarea
              value={form.pickup_instructions}
              onChange={(e) => setForm((f) => ({ ...f, pickup_instructions: e.target.value }))}
              placeholder="e.g. Park in front of the office at #12, ring the bell. Office hours 8am–8pm. After hours: WhatsApp +33 …"
              rows={4}
              className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="vendor-pickup-instructions"
            />
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500 mt-3 block">Pickup map URL (optional)</Label>
            <Input
              type="url"
              value={form.pickup_map_url}
              onChange={(e) => setForm((f) => ({ ...f, pickup_map_url: e.target.value }))}
              placeholder="https://www.google.com/maps/embed?pb=…"
              className="mt-1 rounded-xl"
              data-testid="vendor-pickup-map-url"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2 mb-1.5">
              <MapPin className="h-4 w-4 text-rose-600" />
              <span className="text-sm font-medium">Drop-off instructions</span>
            </Label>
            <textarea
              value={form.dropoff_instructions}
              onChange={(e) => setForm((f) => ({ ...f, dropoff_instructions: e.target.value }))}
              placeholder="e.g. Return to the same parking spot. Leave the key in the office mailbox if the office is closed."
              rows={4}
              className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              data-testid="vendor-dropoff-instructions"
            />
            <Label className="text-[10px] uppercase tracking-wider text-neutral-500 mt-3 block">Drop-off map URL (optional)</Label>
            <Input
              type="url"
              value={form.dropoff_map_url}
              onChange={(e) => setForm((f) => ({ ...f, dropoff_map_url: e.target.value }))}
              placeholder="https://www.google.com/maps/embed?pb=…"
              className="mt-1 rounded-xl"
              data-testid="vendor-dropoff-map-url"
            />
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full mt-6" data-testid="vendor-policies-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save policies
      </Button>
    </div>
  );
}
